import get from 'lodash/get';
import { closeReconciliationURLs } from 'routes/close/closeURLs';

import { UserAvatar } from 'components/data-display/UserAvatar';

import { useUsersData } from 'hooks/useUsersData';

import { getName } from 'utils/users/config';

import { GenericNotificationItem } from 'layout/inbox-notifications/GenericNotificationItem';
import { getDueDateText } from 'layout/inbox-notifications/close/_closeNotificationUtils';

import type {
  ReconTaskUpdatedNotification,
  ReconTaskUpdatedNotificationFields,
} from 'types/inbox-notifications/close/ReconciliationNotifications';

type AssigneeFields = Extract<
  ReconTaskUpdatedNotificationFields,
  `${'preparers' | 'reviewers'}[${number}].user_id`
>;

type AssigneeDueDateFields = Extract<
  ReconTaskUpdatedNotificationFields,
  `${'preparers' | 'reviewers'}[${number}].due_date_time`
>;

export function UpdatedNotificationItem({
  item,
}: {
  item: ReconTaskUpdatedNotification;
}) {
  if (!item.task || !item.old_task) {
    return <div>Reconciliation notification task not found</div>;
  }
  const updatedField = item.updated_fields[0] || '';
  const userProperty = updatedField.split('.').pop() as
    | 'user_id'
    | 'due_date_time';

  switch (userProperty) {
    case 'user_id':
      return (
        <AssigneeNotification
          item={item}
          field={updatedField as AssigneeFields}
        />
      );
    case 'due_date_time':
      return (
        <AssigneeDueDateNotification
          item={item}
          field={updatedField as AssigneeDueDateFields}
        />
      );
    default:
      return <div>Unknown updated field: {userProperty}</div>;
  }
}

function AssigneeNotification({
  item,
  field,
}: {
  item: ReconTaskUpdatedNotification;
  field: AssigneeFields;
}) {
  const { usersMap } = useUsersData();
  const actorName = getName(item.actor_id, usersMap);
  const actorType = field.startsWith('preparers') ? 'preparer' : 'reviewer';
  const oldActorName = getName(get(item.old_task, field), usersMap);
  const newActorName = getName(get(item.task, field), usersMap);

  return (
    <GenericNotificationItem
      item={item}
      url={closeReconciliationURLs.taskBase({
        taskId: item.task.core_snapshot.short_code,
      })}
      avatar={<UserAvatar userId={item.actor_id} />}
      title={`${actorName} assigned you a reconciliation as ${actorType}.`}
      description={`${oldActorName} → ${newActorName}`}
    />
  );
}

function AssigneeDueDateNotification({
  item,
  field,
}: {
  item: ReconTaskUpdatedNotification;
  field: AssigneeDueDateFields;
}) {
  const { usersMap } = useUsersData();
  const actorName = getName(item.actor_id, usersMap);
  const oldActorDueDate = getDueDateText(get(item.old_task, field));
  const newActorDueDate = getDueDateText(get(item.task, field));

  return (
    <GenericNotificationItem
      item={item}
      url={closeReconciliationURLs.taskBase({
        taskId: item.task.core_snapshot.short_code,
      })}
      avatar={<UserAvatar userId={item.actor_id} />}
      title={`${actorName} updated the due date of your reconciliation.`}
      description={`${oldActorDueDate} → ${newActorDueDate}`}
    />
  );
}
